<template>
  <div class="altar-root" :class="{ 'on-home': onHome, 'in-rite': onHome && riteOn, 'home-scrolled': onHome && homeScrolled }">

    <!-- Avatar builder — same form/behavior as the altar image generator. Opened
         from the Mirror's avatar pencil; overlays the panel (the Mystic AIda
         assistant stays available in its own pane for the Advanced tab). -->
    <div v-if="avatarBuilderOpen" class="altar-avatar-builder-pane">
      <AltarBuilder subject="avatar" @done="closeAvatarBuilder" />
    </div>

    <!-- Sign-image builder — same form/behavior, for the seeker's Sun/Rising sign.
         Opened from the Mirror's sign-chip pencils. -->
    <div v-if="signBuilderOpen" class="altar-avatar-builder-pane">
      <AltarBuilder :subject="signSubject" :label="signLabel" @done="closeSignBuilder" />
    </div>


    <!-- Top-level Altar tabs (mirrors Community/Studio). Settings (Horoscope) +
         History were modals from the old hamburger menu — now first-class tabs. -->
    <div v-if="store.querent_id" class="altar-tabs" :aria-hidden="onHome && riteOn ? 'true' : null">
      <!-- The tabs arrive with the stage (navigation-design §13): Home · Journal
           at Stage 1; Mirror joins at the birthday; members have all three. -->
      <button v-if="hasTab('altar')" class="altar-tab" data-testid="altar-tab-home" :class="{ active: altarTab === 'altar' }" @click="selectTab('altar')">Sanctuary</button>
      <button v-if="hasTab('mirror')" class="altar-tab" data-testid="altar-tab-mirror" :class="{ active: altarTab === 'mirror', 'is-new': mirrorDot }" @click="selectTab('mirror')">Mirror<span v-if="mirrorDot" class="altar-tab-dot" data-testid="mirror-tab-dot" aria-label="new"></span></button>
      <button v-if="hasTab('history')" class="altar-tab" data-testid="altar-tab-journal" :class="{ active: altarTab === 'history', 'is-new': journalDot }" @click="selectTab('history')">Journal<span v-if="journalDot" class="altar-tab-dot" data-testid="journal-tab-dot" aria-label="new"></span></button>
    </div>


    <!-- Altar Home (altar redesign 2026-10-27): the painting is the room, the
         intention sits on it like a lit candle, today's sky reads below. The
         first visit is a rite (AltarHome). -->
    <div class="altar-surface altar-home-surface" v-show="altarTab === 'altar'">
      <AltarHome v-if="store.querent_id && hasTab('altar')" ref="homeRef" :active="altarTab === 'altar'" :has-mirror="hasTab('mirror')"
                 @rite="riteOn = $event" @scrolled="homeScrolled = $event" @open-builder="openBuilder('altar')" @open-mirror="selectTab('mirror')" />
    </div>

    <!-- "Shape it further with Mystic AIda" (Repaint): the full builder, its
         Advanced description and the gallery of earlier paintings. -->
    <div v-if="altarBuilderOpen" class="altar-avatar-builder-pane" data-testid="altar-builder-pane">
      <AltarBuilder :subject="builderSubject" @done="closeBuilder" />
    </div>

    <!-- Mirror surface — the seeker's shareable profile. -->
    <div v-show="altarTab === 'mirror'" class="altar-surface">
      <MirrorProfile ref="mirrorRef" />
    </div>

    <!-- Reading History tab (was a modal) — now with search. -->
    <!-- The Journal: a book the seeker keeps (journal redesign 2026-10-27). -->
    <div v-show="altarTab === 'history'" class="altar-surface altar-tabpane altar-journal-pane">
      <ReadingHistory :searchable="true" :initialDay="initialDay"
                      @dayChange="$emit('dayChange', $event)" @seen="journalRead = true" />
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { store } from '@/store'
import { mirrorJustArrived } from '@/utils/birth-ask'
import { getQuerent, getJournalStatus } from '@/api-service'
import ReadingHistory from '@/components/ui/modals/ReadingHistory.vue'
import AltarHome from '@/components/altar/AltarHome.vue'
import AltarBuilder from '@/components/ui/AltarBuilder.vue'
import MirrorProfile from '@/components/ui/MirrorProfile.vue'
import { signSubject as toSignSubject, signLabel as toSignLabel } from '@/utils/chart-signs'

const altarTab = ref('altar')
const mirrorRef = ref(null)
const homeRef = ref(null)
// The first-visit rite is under way: the sub-tabs step back until it ends.
const riteOn = ref(false)
// Home has scrolled off the top: the tab strip becomes a dark band (design-batch walk 2026-10-30, A).
const homeScrolled = ref(false)
const onHome = computed(() => altarTab.value === 'altar' && !!store.querent_id)

// Tapping any tab signals surfaces (e.g. the Journal editor) to exit their
// focused state, even when re-tapping the current tab.
function selectTab(tab) {
  altarTab.value = tab
  window.dispatchEvent(new CustomEvent('altar-tab-changed', { detail: tab }))
  if (tab !== 'history') checkJournal()
}
const altarBuilderOpen = ref(false)
const builderSubject = ref('altar')   // 'altar' | 'keeper'
const avatarBuilderOpen = ref(false)  // the Mirror avatar builder (overlay)
function onOpenAvatarBuilder() { avatarBuilderOpen.value = true }
// Editing the avatar must NOT leave the avatar lingering as the Altar Home image:
// re-derive the altar image from the DB (altar subject only) so an empty altar
// falls back to the default starry scene.
function closeAvatarBuilder() {
  avatarBuilderOpen.value = false
}

// Sign-image builder (Sun / Moon / Rising), opened from the Mirror sign-chip
// pencils. The subject and label come from @/utils/chart-signs so a new sign
// paints into its own subject instead of falling through to the Sun's.
const signBuilderOpen = ref(false)
const signSubject = ref('sun_sign')   // 'sun_sign' | 'moon_sign' | 'rising_sign'
const signLabel = ref('')             // e.g. "Gemini Moon"
function onOpenSignBuilder(e) {
  const d = e.detail || {}
  signSubject.value = toSignSubject(d.which)
  signLabel.value = toSignLabel(d.sign, d.which)
  altarTab.value = 'mirror'
  signBuilderOpen.value = true
}
function closeSignBuilder() {
  signBuilderOpen.value = false
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
  if (mirrorRef.value) mirrorRef.value.load()   // refresh the chips with the new image
}
const props = defineProps({
  querentName: String,
  querentSubscribed: Boolean,
  altarImageUrl: { type: String, default: null },
  // Bumped by the parent when the Altar nav tab is tapped — reset to Home.
  homeSignal: { type: Number, default: 0 },
  // Quest "Go" deep-link: { category, n } — route to the matching surface.
  questTarget: { type: Object, default: null },
  // Sub-tab to restore on mount (from the URL ?tab=). One of the altarTab keys.
  initialTab: { type: String, default: '' },
  // The Journal day (YYYY-MM-DD) to open on, from the URL ?day=.
  initialDay: { type: String, default: '' },
  // The tabs this person has (the server's altar_tabs), and where a tap on
  // the Altar lands (altar_first_tab). Members: all three, Home.
  tabs: { type: Array, default: () => ['altar', 'mirror', 'history'] },

  landTab: { type: String, default: '' },
  // A new room inside (2026-11-03 §14): 'mirror' puts a quiet dot on the Mirror tab until it is opened.
  roomDot: { type: String, default: '' },
})
const hasTab = (t) => props.tabs.includes(t)

// Tapping the Altar nav (even when already on the Altar surface) returns to Home.
// The Quest journey: before the Moon there is no Home, so the fallback is the
// first room they have; before the Journal, none (never a Home they lack: prod
// 2026-10-30 opened the Moon's rite at First Star).
const firstTab = () => (hasTab('altar') ? 'altar' : (props.tabs[0] || ''))
watch(() => props.homeSignal, () => { altarTab.value = props.landTab && hasTab(props.landTab) ? props.landTab : firstTab() })
// A tab they don't have yet (a stale ?tab=mirror before a birthday) is Home.
// A fallback is quiet: it never writes the URL (it raced ?panel=quest on load).
let quietTab = ''
watch(() => props.tabs, () => { if (!hasTab(altarTab.value)) { quietTab = firstTab(); altarTab.value = quietTab } }, { immediate: true })
// The birthday brings the Mirror (navigation-design §13): it arrives with a
// dot and a glow, once, until it is opened (birthday ask 2026-10-26).
const mirrorNew = ref(false)
const mirrorOpened = ref(false)
const mirrorDot = computed(() => mirrorNew.value || (props.roomDot === 'mirror' && !mirrorOpened.value))
// The Journal's gold dot: a new entry written overnight, not yet read
// (journal/status/ `unseen`, not behind the clasp). No push, no email.
const journalUnseen = ref(false)
const journalRead = ref(false)
const journalDot = computed(() => journalUnseen.value && !journalRead.value)
watch(() => [store.session_id, store.querent_id], ([s, q]) => { if (s && q) checkJournal() })
async function checkJournal() {
  if (!store.session_id || !store.querent_id) return
  try { const s = await getJournalStatus(store.session_id); journalUnseen.value = !!(s && s.unseen); if (journalUnseen.value) journalRead.value = false } catch (e) { /* no dot */ }
}
watch(() => props.tabs, (now, before) => { if (mirrorJustArrived(before, now)) mirrorNew.value = true })
watch(altarTab, (t) => { if (t === 'mirror') { mirrorNew.value = false; mirrorOpened.value = true } }, { immediate: true })
// "Open my Mirror" on the birthday ask's big three.
function onOpenMirror() { if (hasTab('mirror')) altarTab.value = 'mirror' }

// Route a quest "Go" to the right Altar surface + open the relevant editor.
const QUEST_TAB = {
  profile_name: 'mirror', profile_avatar: 'mirror', profile_signs: 'mirror',
  profile_affirmation: 'altar', profile_altar: 'altar', first_reading: 'altar',
  share_reading: 'history',
}
function routeQuest(t) {
  if (!t || !t.category) return
  // Messages are global now — invite_friend opens the global panel, not a tab.
  if (t.category === 'invite_friend') { store.messages_open = true; return }
  altarTab.value = QUEST_TAB[t.category] || 'altar'
  nextTick(() => {
    const c = t.category
    if (c === 'profile_avatar') window.dispatchEvent(new CustomEvent('open-avatar-builder'))
    else if (c === 'profile_name') window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'name' } }))
    else if (c === 'profile_signs') window.dispatchEvent(new CustomEvent('open-birth-modal'))
    // "Set your intention" opens the affirmation chat (Magic / Spoken), as it
    // did before the redesign; "Paint your Altar" opens the builder.
    else if (c === 'profile_affirmation') window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'affirmation' } }))
    else if (c === 'profile_altar') { if (homeRef.value) homeRef.value.openPaint() }
  })
}
watch(() => props.questTarget, (t) => routeQuest(t))

function openBuilder(subject = 'altar') {
  // The assistant only opens on the Advanced tab (driven inside AltarBuilder),
  // so opening the builder itself does not summon it.
  builderSubject.value = subject
  altarBuilderOpen.value = true
}
function closeBuilder() {
  altarBuilderOpen.value = false
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
}

// The Keeper edit pencil (in the assistant) SWAPS the current form for the keeper
// builder: close the horoscope form if it's open, then open the keeper builder.
function onOpenBuilder(e) {
  const subject = (e.detail && e.detail.subject) || 'altar'
  openBuilder(subject)
}

// The horoscope's "Add your birth details" prompt → the full modal lives on the
// Mirror, so switch there and open it (same path as the profile_signs quest).
function onOpenBirthDetails() {
  altarTab.value = 'mirror'
  nextTick(() => window.dispatchEvent(new CustomEvent('open-birth-modal')))
}

onMounted(() => {
  checkJournal()
  window.addEventListener('altar-open-builder', onOpenBuilder)
  window.addEventListener('open-avatar-builder', onOpenAvatarBuilder)
  window.addEventListener('open-sign-builder', onOpenSignBuilder)
  window.addEventListener('open-birth-details', onOpenBirthDetails)
  window.addEventListener('altar-open-mirror', onOpenMirror)
  // If we mounted because of a quest "Go", route to its surface.
  if (props.questTarget) routeQuest(props.questTarget)
  // Otherwise restore the sub-tab the URL asked for (refresh / shared link).
  else if (props.initialTab && ALTAR_TABS.includes(props.initialTab) && hasTab(props.initialTab)) {
    altarTab.value = props.initialTab
  }
})
onUnmounted(() => {
  window.removeEventListener('altar-open-builder', onOpenBuilder)
  window.removeEventListener('open-avatar-builder', onOpenAvatarBuilder)
  window.removeEventListener('open-sign-builder', onOpenSignBuilder)
  window.removeEventListener('open-birth-details', onOpenBirthDetails)
  window.removeEventListener('altar-open-mirror', onOpenMirror)
})

const emit = defineEmits(['close', 'updateQuerentName', 'triggerAstrology', 'tabChange', 'dayChange'])

// Mirror the active sub-tab up to the URL so a refresh restores it. Catches every
// path that changes the tab (taps, quest "Go", sign-builder), not just selectTab.
const ALTAR_TABS = ['altar', 'mirror', 'history']
watch(altarTab, (t) => { if (t === quietTab) { quietTab = ''; return } quietTab = ''; emit('tabChange', t) })

const zodiacSign = computed(() => {
  if (!store.querent_birthdate) return null
  try {
    const parts = store.querent_birthdate.split('-').map(Number)
    return getZodiacSign(parts[1], parts[2])
  } catch { return null }
})

function getZodiacSign(month, day) {
  const signs = [
    [[1,20],[2,18],'Aquarius'], [[2,19],[3,20],'Pisces'],
    [[3,21],[4,19],'Aries'],    [[4,20],[5,20],'Taurus'],
    [[5,21],[6,20],'Gemini'],   [[6,21],[7,22],'Cancer'],
    [[7,23],[8,22],'Leo'],      [[8,23],[9,22],'Virgo'],
    [[9,23],[10,22],'Libra'],   [[10,23],[11,21],'Scorpio'],
    [[11,22],[12,21],'Sagittarius'],
  ]
  for (const [[sm,sd],[em,ed],sign] of signs) {
    if ((month===sm && day>=sd) || (month>sm && month<em) || (month===em && day<=ed)) return sign
  }
  return 'Capricorn'
}

watch(altarTab, async (tab) => {
  // History + Settings both want fresh querent data (readings, birth details).
  if ((tab === 'history' || tab === 'settings') && store.querent_id) {
    try { await getQuerent(store.querent_id) } catch (e) { console.error(e) }
  }
  // Mirror reflects shared readings + altar share state — refresh on open.
  if (tab === 'mirror' && mirrorRef.value) mirrorRef.value.load()
})
</script>

<style scoped>
.altar-avatar-builder-pane {
  position: absolute;
  inset: 0;
  z-index: 20;
  overflow-y: auto;
  background: #08000f;
  padding: 16px 14px 28px;
}
.altar-root {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: #08000f;
  color: white;
  position: relative;
  overflow-y: auto;
}

/* Menu button */
.menu-btn {
  position: absolute; top: 10px; right: 12px; z-index: 20;
  background: rgba(102,51,153,0.35); border: 1px solid rgba(102,51,153,0.4);
  color: rgba(255,255,255,0.6); width: 32px; height: 32px;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 13px; transition: all 0.2s;
}
.menu-btn:hover { background: rgba(102,51,153,0.6); color: #FFDFA3; }

/* Quick menu dropdown */
.quick-menu {
  position: absolute; top: 48px; right: 12px; z-index: 20;
  background: #1a0a2e; border: 1px solid rgba(102,51,153,0.3);
  border-radius: 10px; overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  min-width: 180px;
}
.quick-menu-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 12px 16px; border: none; background: none;
  color: rgba(255,255,255,0.6); font-size: 13px; font-family: inherit;
  cursor: pointer; text-align: left; transition: all 0.15s;
}
.quick-menu-item:hover { background: rgba(102,51,153,0.2); color: #FFDFA3; }
.quick-menu-item + .quick-menu-item { border-top: 1px solid rgba(102,51,153,0.15); }
.quick-menu-item i { font-size: 12px; width: 16px; text-align: center; color: rgba(154,113,189,0.5); }

/* Altar modals */
.altar-modal-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(0,0,0,0.7);
  display: flex; align-items: center; justify-content: center;
  animation: fadeIn 0.15s ease;
}
.altar-modal {
  background: #1a0a2e; border: 1px solid rgba(102,51,153,0.3);
  border-radius: 16px; width: 92vw; max-width: 520px; max-height: 85vh;
  display: flex; flex-direction: column;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  overflow: hidden;
}
.altar-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid rgba(102,51,153,0.2);
}
.altar-modal-header h3 {
  font-size: 16px; font-weight: 600; color: rgba(255,223,163,0.85); margin: 0;
}
.altar-modal-close {
  background: none; border: none; color: rgba(255,255,255,0.4);
  font-size: 22px; cursor: pointer; line-height: 1; padding: 0;
}
.altar-modal-close:hover { color: #fff; }
.altar-tabpane { flex: 1; overflow-y: auto; padding: 0; background: #1a0a2e; }
/* The book lays its pages in a fixed frame: the pane is that frame. */
.altar-journal-pane { position: relative; overflow: hidden; min-height: 480px; background: #0d0520; }

/* Account tab — a calm, centered card (matches Home / Mirror, not the full-bleed form). */
.altar-account { padding: 26px 16px 48px; }
.altar-account-card { max-width: 520px; margin: 0 auto;
  background: linear-gradient(160deg, rgba(93,59,140,0.18), rgba(28,16,48,0.42));
  border: 1px solid rgba(155,134,201,0.25); border-radius: 16px; padding: 24px 26px 26px; }
.altar-account-title { font-family: 'Cinzel', serif; font-size: 20px; color: #f5e8c0; margin: 0 0 16px; }
.altar-account :deep(.subscription-badge) { display: inline-block; margin-bottom: 16px;
  color: #f0d9a3 !important; background: rgba(240,217,163,0.1) !important;
  border: 1px solid rgba(240,217,163,0.28); border-radius: 999px; padding: 4px 12px; font-size: 12px !important; }
.altar-account :deep(label) { font-size: 11px !important; letter-spacing: 0.06em; }
.altar-account :deep(.submit-button) {
  background: linear-gradient(180deg, #7b4bb0, #4e2776) !important; color: #fff !important;
  border: none !important; border-radius: 999px !important; padding: 12px 20px !important;
  font-weight: 700 !important; box-shadow: 0 8px 20px -10px rgba(78,39,118,0.9) !important; margin-top: 14px !important; }
.altar-account :deep(.submit-button:hover) { filter: brightness(1.08); background: linear-gradient(180deg, #7b4bb0, #4e2776) !important; }
.altar-account :deep(.version-tag) { text-align: center; margin-top: 14px; }

/* Dark theme overrides for AccountManagement inside altar modal */
.altar-tabpane :deep(.modal.menu-modal),
.altar-tabpane :deep(.modal) {
  position: static !important; top: auto !important; left: auto !important;
  transform: none !important; width: 100% !important; max-width: none !important;
  max-height: none !important; background: transparent !important;
  box-shadow: none !important; border-radius: 0 !important;
  overflow-y: visible !important; z-index: auto !important;
  align-items: stretch !important;
}
.altar-tabpane :deep(.modal-content) {
  background-color: transparent !important; background: transparent !important;
  padding: 12px 16px !important; border-radius: 0 !important;
  min-height: auto !important; box-shadow: none !important;
}
.altar-tabpane :deep(.close-btn) { display: none !important; }
.altar-tabpane :deep(.account-header h3) { display: none !important; }
.altar-tabpane :deep(.account-header) { margin-bottom: 4px !important; }
.altar-tabpane :deep(.subscription-badge) { color: rgba(255,223,163,0.6) !important; background: rgba(102,51,153,0.3) !important; font-size: 12px !important; }
.altar-tabpane :deep(label) { color: rgba(255,255,255,0.45) !important; font-size: 11px !important; }
.altar-tabpane :deep(input[type="text"]),
.altar-tabpane :deep(input[type="email"]),
.altar-tabpane :deep(input[type="date"]),
.altar-tabpane :deep(input[type="time"]) {
  background: rgba(102,51,153,0.12) !important; border: 1px solid rgba(102,51,153,0.3) !important;
  color: #e0d0ff !important; border-radius: 8px !important; padding: 8px 10px !important; font-size: 13px !important;
  font-family: inherit !important; width: 100% !important; box-sizing: border-box !important;
}
.altar-tabpane :deep(input:focus) { border-color: rgba(102,51,153,0.5) !important; outline: none !important; background: rgba(102,51,153,0.2) !important; }
.altar-tabpane :deep(.birthdate-inputs) { display: flex !important; gap: 8px !important; }
.altar-tabpane :deep(.birthdate-inputs input) { flex: 1 !important; min-width: 0 !important; }
.altar-tabpane :deep(.submit-button) {
  background: rgba(102,51,153,0.25) !important; border: 1px solid rgba(102,51,153,0.35) !important;
  color: rgba(255,223,163,0.8) !important; border-radius: 10px !important; padding: 10px 20px !important;
  font-size: 13px !important; cursor: pointer !important; font-family: inherit !important; width: 100% !important; margin-top: 8px !important;
}
.altar-tabpane :deep(.submit-button:hover) { background: rgba(102,51,153,0.4) !important; color: #FFDFA3 !important; }
.altar-tabpane :deep(.form-layout) { display: flex !important; flex-direction: column !important; gap: 0 !important; }
.altar-tabpane :deep(.form-fields) { width: 100% !important; flex: none !important; }
.altar-tabpane :deep(.form-map) { width: 100% !important; flex: none !important; margin-top: 4px !important; }
.altar-tabpane :deep(.form-section) { margin-bottom: 10px !important; }
.altar-tabpane :deep(.map-widget-wrapper) {
  height: 180px !important; border-radius: 8px !important; overflow: hidden !important;
  border: 1px solid rgba(102,51,153,0.2) !important; flex: none !important;
}
.altar-tabpane :deep(.modal),
.altar-tabpane :deep(.modal-content),
.altar-tabpane :deep(.form-layout),
.altar-tabpane :deep(.form-fields),
.altar-tabpane :deep(.form-map),
.altar-tabpane :deep(.form-section),
.altar-tabpane :deep(.account-header),
.altar-tabpane :deep(.birthdate-widget) {
  background-color: transparent !important; background: transparent !important;
}
.altar-tabpane :deep(.version-tag) { color: rgba(255,255,255,0.1) !important; font-size: 10px !important; }
/* Color scheme for date/time picker icons */
.altar-tabpane :deep(input[type="date"])::-webkit-calendar-picker-indicator,
.altar-tabpane :deep(input[type="time"])::-webkit-calendar-picker-indicator {
  filter: invert(0.7);
}

.altar-surface { display: flex; flex-direction: column; flex: 1; }
/* Altar Home (altar redesign 2026-10-27): the painting is the room, so Home
   fills the panel and scrolls itself; the sub-tabs float over the painting
   and step back during the first-visit rite (they return last). */
.altar-root.on-home { height: 100%; min-height: 0; overflow: hidden; }
.altar-root.on-home .altar-home-surface { position: relative; flex: 1; min-height: 0; }
.altar-root.on-home .altar-tabs { position: absolute; top: 0; left: 0; right: 0; z-index: 8; background: transparent;
  transition: opacity .8s ease .2s; }
/* Once Home scrolls (the paint form, the everyday words), the strip is a dark
   band in the Altar's own colour, so nothing prints under the tab labels; at
   rest it still floats clear over the painting (design-batch walk 2026-10-30). */
.altar-root.on-home .altar-tabs::before { content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none; opacity: 0;
  background: linear-gradient(180deg, #170a33 0%, #0d0520 100%); box-shadow: 0 8px 16px rgba(13,5,32,.8); transition: opacity .25s ease; }
.altar-root.on-home.home-scrolled .altar-tabs::before { opacity: 1; }
.altar-root.on-home .altar-tab { color: rgba(195,169,255,0.6); text-shadow: 0 1px 6px rgba(0,0,0,0.9); }
.altar-root.on-home .altar-tab.active { color: #FFDFA3; }
.altar-root.in-rite .altar-tabs { opacity: 0; pointer-events: none; transition-delay: 0s; }
@media (prefers-reduced-motion: reduce) { .altar-root.on-home .altar-tabs { transition-duration: .2s; transition-delay: 0s; } }
.altar-messages { padding: 8px 14px 14px; overflow-y: auto; }
/* Tab bar — unified app menu: centered, content-width tabs (clear of the floating
   corner widgets), spread by a responsive gap to fill the band. Shared by Altar,
   Community, Quest and Studio. */
.altar-tabs { display: flex; justify-content: center; gap: clamp(12px, 6vw, 72px);
  border-bottom: 1px solid rgba(93,59,140,0.25); padding: 8px 16px 0; }
.altar-tab { flex: 0 0 auto; background: transparent; border: none; border-bottom: 2px solid transparent;
  color: rgba(195,169,255,0.4); font-size: 14px; font-family: 'Cinzel', serif; padding: 8px 18px 10px;
  cursor: pointer; text-align: center; white-space: nowrap; transition: all 0.2s; }
.altar-tab:hover { color: rgba(195,169,255,0.7); }
.altar-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.altar-tab { position: relative; }
/* A 44px touch target past the 39px tab and its underline (e2e round 2). */
.altar-tab::after { content: ''; position: absolute; inset: -5px 0; }
.altar-tab.is-new { animation: altar-tab-glow 2.2s ease 1; }
.altar-tab-dot { position: absolute; top: 6px; right: 6px; width: 7px; height: 7px; border-radius: 50%; background: #FFDFA3;
  box-shadow: 0 0 8px #FFDFA3; }
@keyframes altar-tab-glow { 0% { opacity: 0; transform: translateY(-6px); text-shadow: none } 40% { opacity: 1; transform: none; text-shadow: 0 0 14px #FFDFA3 } 100% { text-shadow: none } }
@media (prefers-reduced-motion: reduce) { .altar-tab.is-new { animation: none; } }
/* The gold underline is the active indicator — drop the focus box so Journal /
   Account match Home / Mirror after a tap. */
.altar-tab:focus, .altar-tab:focus-visible,
.altar-msg-btn:focus, .altar-msg-btn:focus-visible { outline: none; }
/* Messages icon — with an unread/requests count. */
.altar-msg-btn { position: relative; background: transparent; border: none;
  color: #9b86c9; font-size: 16px; cursor: pointer; padding: 4px 6px 8px; }
.altar-msg-btn:hover, .altar-msg-btn.active { color: #f0d9a3; }
/* Floated out of the tab box → top-right, just left of the exit (sign-out). */
.altar-msg-float { position: fixed; top: 0; right: 50px; z-index: 1001; font-size: 18px; padding: 10px 12px; }
.altar-msg-badge { position: absolute; top: -2px; right: -4px; min-width: 16px; height: 16px; padding: 0 4px;
  border-radius: 999px; background: #c0392b; color: #fff; font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; line-height: 1; }

/* Centered, comfortably spaced Altar Home column. */
.altar-home-col { width: 100%; max-width: 640px; margin: 0 auto; padding: 12px 16px 14px;
  /* Explicit: src/assets/base.css (which carries the global border-box reset)
     is never imported by main.js, so this element is content-box. width:100%
     plus 16px side padding therefore measured 32px wider than the viewport and
     every card inside it hung off the right edge. */
  box-sizing: border-box;
  display: flex; flex-direction: column; gap: 14px; }

/* Slow, staggered fade-in on load: affirmation (1) → altar (2) → horoscope (3). */
.altar-reveal { opacity: 0; transform: translateY(12px);
  transition: opacity 0.9s ease, transform 0.9s ease; will-change: opacity, transform; }
.altar-home-col.revealed .altar-reveal { opacity: 1; transform: translateY(0); }
.altar-home-col.revealed .reveal-1 { transition-delay: 0.1s; }
.altar-home-col.revealed .reveal-2 { transition-delay: 0.7s; }
.altar-home-col.revealed .reveal-3 { transition-delay: 1.3s; }
@media (prefers-reduced-motion: reduce) {
  .altar-reveal { transition: none; transform: none; opacity: 1; }
}
/* Only when even the smallest altar will not fit: the reading scrolls inside
   its own card, so the surface stays a single screen. */
.altar-reveal.reveal-scroll { min-height: 0; overflow-y: auto; }
.altar-image-zone { width: 100%; flex-shrink: 0; display: flex;
  justify-content: center; align-items: center; background: #08000f; position: relative; }
/* Was absolutely positioned over the drawn-altar placeholder. The placeholder
   is gone (owner call: it was not carrying its weight, and its fixed height
   was the last thing pushing Altar Home below the fold), so the invitation is
   now a compact block that sizes to itself. */
.altar-create-invite { display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 8px; padding: 18px 0 6px; }
/* As a card, framed like the intention and the stars (§13, S1d). */
.altar-create-invite.altar-home-card { width: 100%; box-sizing: border-box; padding: 20px 18px;
  background: radial-gradient(80% 120% at 50% 0%, rgba(93,59,140,0.30), rgba(28,16,48,0.35));
  border: 1px solid rgba(240,217,163,0.18); border-radius: 14px; }
.altar-create-btn { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 12px 26px; font-size: 15px; font-weight: 700; cursor: pointer;
  box-shadow: 0 10px 26px -10px rgba(78,39,118,0.95); font-family: inherit; }
.altar-create-btn:hover { filter: brightness(1.08); }
.altar-create-hint { font-size: 12px; color: rgba(233,220,255,0.7); font-style: italic; margin: 0; text-shadow: 0 1px 4px #000; }
/* Ghost edit icon — faint until you hover the altar image. */
.altar-customize-cog { position: absolute; top: 12px; right: 12px; width: 26px; height: 26px; border: none;
  background: none; color: #f0d9a3; cursor: pointer; font-size: 14px; opacity: 0.35;
  transition: opacity 0.2s; text-shadow: 0 1px 4px rgba(0,0,0,0.9); }
.altar-image-zone:hover .altar-customize-cog, .altar-customize-cog:hover { opacity: 1; }

/* Soft fade between the altar image and the customization panel. */
.altar-builder-pane { padding: 14px 16px 20px; }
.altar-fade-enter-active, .altar-fade-leave-active { transition: opacity 0.3s ease; }
.altar-fade-enter-from, .altar-fade-leave-to { opacity: 0; }

/* Sized by HEIGHT (max-height set dynamically in JS) so the altar fills exactly
   the space left between the affirmation and horoscope — width follows to keep
   aspect, centered by the image-zone. 60vh is a pre-JS fallback. */
.altar-dalle-image { max-width: 100%; max-height: 60vh; width: auto; height: auto; display: block;
  object-fit: contain; object-position: center; border-radius: 16px;
  box-shadow: 0 16px 40px -16px rgba(0,0,0,0.8);
  border: 1px solid rgba(240,217,163,0.15); cursor: zoom-in; }

.keeper-presence { display: flex; align-items: center; gap: 10px; }
.presence-pulse { width: 8px; height: 8px; border-radius: 50%; background: #663399; animation: pulse 2s ease-in-out infinite; flex-shrink: 0; }
@keyframes pulse { 0%,100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.4); } }
.presence-label { font-size: 12px; color: rgba(255,255,255,0.3); letter-spacing: 0.8px; }

/* Altar objects */
/* Reading image gallery */
.reading-gallery {
  padding: 12px 16px 24px;
  margin-top: 8px;
  border-top: 1px solid rgba(102,51,153,0.15);
}
.gallery-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.gallery-label { font-size: 10px; color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 0.8px; }
.gallery-count { font-size: 11px; color: rgba(154,113,189,0.4); }
.gallery-carousel { display: flex; align-items: center; gap: 4px; }
.gallery-arrow {
  flex-shrink: 0; width: 28px; height: 28px; border: none; border-radius: 50%;
  background: rgba(255,255,255,0.08); color: #9a71bd; font-size: 20px; line-height: 1;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.gallery-arrow:hover { background: rgba(255,255,255,0.16); }
.gallery-track {
  flex: 1;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 4px 0;
}
.gallery-track::-webkit-scrollbar { display: none; }
.gallery-item {
  width: 220px; height: 220px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(102,51,153,0.15);
  flex-shrink: 0;
  scroll-snap-align: start;
  transition: border-color 0.2s, transform 0.2s;
  position: relative;
}
.gallery-item:hover { border-color: rgba(102,51,153,0.4); transform: scale(1.03); }
.gallery-item img { width: 100%; height: 100%; object-fit: cover; object-position: center bottom; display: block; }
.gallery-item-overlay {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  padding: 20px 8px 6px;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.gallery-item-title {
  font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.9); line-height: 1.3;
  overflow: hidden; text-overflow: ellipsis;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  text-align: center;
}
.gallery-item-date { font-size: 11px; color: rgba(255,255,255,0.45); text-align: center; }

/* Reading detail lightbox */
.reading-detail {
  background: #1a0a2e; border: 1px solid rgba(102,51,153,0.3);
  border-radius: 16px; max-width: 400px; width: 90vw;
  overflow: hidden; position: relative;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  cursor: default;
}
.reading-detail-img { width: 100%; display: block; }
.reading-detail-body { padding: 14px 16px; }
.reading-detail-title { font-size: 15px; font-weight: 600; color: rgba(255,223,163,0.85); margin: 0 0 8px; }
.reading-detail-question { font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.5; margin: 0 0 6px; font-style: italic; }
.reading-detail-date { font-size: 11px; color: rgba(154,113,189,0.5); }

/* Horoscope widget */
.horoscope-widget {
  margin: 8px 16px 0;
  border-radius: 10px;
  border: 1px solid rgba(102,51,153,0.2);
  background: rgba(102,51,153,0.05);
  overflow: hidden;
}
.horoscope-content { display: flex; flex-direction: column; }

/* Drop widget */
.drop-widget { padding: 14px 16px; border-bottom: 1px solid rgba(102,51,153,0.12); }
.drop-title { font-size: 16px; font-weight: 600; color: rgba(255,223,163,0.85); margin: 0 0 12px; text-align: center; }
.drop-three-col { display: flex; gap: 12px; align-items: center; }
.drop-img { width: 120px; height: 120px; border-radius: 10px; overflow: hidden; border: 1px solid rgba(102,51,153,0.2); flex-shrink: 0; cursor: pointer; transition: border-color 0.2s; }
.drop-img:hover { border-color: rgba(102,51,153,0.5); }
.drop-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.drop-center { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; min-width: 0; }
.drop-reading { font-size: 14px; color: rgba(255,255,255,0.55); line-height: 1.65; margin: 0; text-align: center; }
.drop-card-col { display: flex; flex-direction: column; align-items: center; gap: 5px; flex-shrink: 0; cursor: pointer; }
.drop-tarot-name { font-size: 11px; color: rgba(154,113,189,0.55); text-align: center; }
.drop-tarot-orientation { font-size: 9px; color: rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 0.5px; }
.drop-tarot-img.reversed img { transform: rotate(180deg); }
.tarot-modal-img.reversed img { transform: rotate(180deg); }
.tarot-modal-orient { font-size: 11px; color: rgba(255,223,163,0.5); text-transform: uppercase; letter-spacing: 0.8px; }
.drop-tarot-img { width: 86px; height: 120px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(102,51,153,0.25); }
.drop-tarot-img img { width: 100%; height: 100%; object-fit: cover; display: block; }

.horoscope-collective { padding: 14px 16px 10px; border-bottom: 1px solid rgba(102,51,153,0.12); }
.horoscope-date-label { font-size: 10px; color: rgba(255,255,255,0.25); text-transform: uppercase; letter-spacing: 0.8px; display: block; margin-bottom: 5px; }
.horoscope-collective-text { font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.6; font-style: italic; margin: 0; }
.horoscope-sign-block { padding: 10px 16px; border-bottom: 1px solid rgba(102,51,153,0.12); }
.horoscope-sign-label { font-size: 10px; color: rgba(255,223,163,0.5); text-transform: uppercase; letter-spacing: 0.8px; display: block; margin-bottom: 5px; }
.horoscope-sign-text { font-size: 13px; color: rgba(255,223,163,0.75); line-height: 1.65; font-style: italic; margin: 0; }
.horoscope-keeper-cta { padding: 10px 16px 12px; }
.horoscope-keeper-btn {
  background: rgba(102,51,153,0.15); border: 1px solid rgba(102,51,153,0.35); color: rgba(255,223,163,0.7);
  padding: 10px 16px; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 500;
  transition: all 0.2s; width: 100%; font-family: inherit;
}
.horoscope-keeper-btn:hover { border-color: rgba(102,51,153,0.6); color: #FFDFA3; background: rgba(102,51,153,0.25); }
.horoscope-setup-btn {
  background: rgba(102,51,153,0.08); border: 1px solid rgba(102,51,153,0.2); color: rgba(255,255,255,0.4);
  padding: 10px 16px; border-radius: 10px; cursor: pointer; font-size: 13px;
  transition: all 0.2s; width: 100%; font-family: inherit;
}
.horoscope-setup-btn:hover { border-color: rgba(102,51,153,0.4); color: rgba(255,255,255,0.6); background: rgba(102,51,153,0.15); }
.horoscope-pending, .horoscope-loading { padding: 14px 16px; display: flex; align-items: center; gap: 10px; }
.horoscope-pending-label { font-size: 12px; color: rgba(255,255,255,0.25); font-style: italic; }

/* Embed chrome stripping */
.altar-embed :deep(.modal), .altar-embed :deep(.menu-modal) {
  position: static !important; transform: none !important; width: 100% !important;
  max-width: 100% !important; max-height: none !important; box-shadow: none !important;
  z-index: auto !important; top: auto !important; left: auto !important; overflow: visible !important;
}
.altar-embed :deep(.modal-content) { margin-top: 0 !important; min-height: auto !important; padding: 0 !important; }
.altar-embed :deep(.x-modal-header), .altar-embed :deep(.close-btn) { display: none !important; }
/* Lightbox */
.lightbox-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  animation: fadeIn 0.2s ease;
}
.lightbox-img {
  max-width: 90vw; max-height: 85vh;
  border-radius: 12px;
  box-shadow: 0 0 40px rgba(102,51,153,0.3);
  cursor: default;
}
.lightbox-close {
  position: absolute; top: 16px; right: 20px;
  background: none; border: none;
  color: rgba(255,255,255,0.6); font-size: 32px;
  cursor: pointer; line-height: 1;
}
.lightbox-close:hover { color: #fff; }
/* Tarot modal */
.tarot-modal {
  background: #1a0a2e;
  border: 1px solid rgba(102,51,153,0.3);
  border-radius: 16px;
  max-width: 420px;
  width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  padding: 24px 20px;
  position: relative;
  box-shadow: 0 0 40px rgba(102,51,153,0.3);
  animation: fadeIn 0.2s ease;
}
.tarot-modal-top { display: flex; gap: 16px; margin-bottom: 16px; }
.tarot-modal-img { width: 100px; height: 150px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(102,51,153,0.3); flex-shrink: 0; }
.tarot-modal-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tarot-modal-header { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 8px; }
.tarot-modal-name { font-size: 20px; font-weight: 600; color: rgba(255,223,163,0.9); margin: 0; }
.tarot-modal-desc { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.6; margin: 0; }
.tarot-modal-section { padding-top: 14px; border-top: 1px solid rgba(102,51,153,0.15); margin-top: 14px; }
.tarot-modal-label { font-size: 10px; color: rgba(255,223,163,0.5); text-transform: uppercase; letter-spacing: 0.8px; display: block; margin-bottom: 6px; }
.tarot-modal-text { font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.65; margin: 0; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

/* Phone-only fixes (~390px): stack the crushed drop row, right-size gallery tiles,
   clear the fixed bottom nav, and give tabs a proper tap target. Desktop unchanged. */
@media (max-width: 480px) {
  /* Drop row: stack image + reading + card into a column so nothing gets crushed. */
  .drop-three-col { flex-wrap: wrap; flex-direction: column; }
  .drop-img { width: 100%; height: auto; aspect-ratio: 1 / 1; }

  /* Gallery tiles: shrink so a second tile hints at the edge. */
  .gallery-item { width: min(220px, 60vw); height: min(220px, 60vw); }

  /* Trailing content clears the fixed bottom nav. .panel-zone already reserves
     70px for it, so the old 90px here double-booked the same space and left
     ~133px of dead air under the last card at full scroll. 24px is the gap
     between the card and the menu, nothing more. */
  .altar-home-col { padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px)); }

  /* Tabs: bump to a 44px tap target. */
  .altar-tab { min-height: 44px; display: flex; align-items: center; justify-content: center; }

  /* Clear the account portrait (top-right, fixed). The tab row is centred
     across the full width, so on a phone "Journal" slid under it. Same fix
     .community-tabs uses for the identical collision: the row starts below
     the portrait rather than cramming three tabs into the gap beside it. */
  .altar-tabs { padding-top: var(--tra-clear-top, 60px); }
  .altar-root.on-home .altar-tabs { border-bottom-color: transparent; }
}
</style>
